import { Injectable } from "@angular/core";
import { RouterLink } from "@angular/router";
import { environment } from "environments/environment";
import { MenuItem } from "primeng/api";
import { BehaviorSubject, Observable } from "rxjs";

// Single location to update all labels, icons, and routerLinks for navigation items.
export const NavigationItems = Object.freeze<Record<string, MenuItem>>({
  Home: {
    label: "Dashboard",
    routerLink: ["/dashboard"],
    icon: "fa  fa-house",
  },
  References: {
    label: "References",
    routerLink: ["/references/manage/manufacturers"],
    icon: "fa fa-conveyor-belt-arm",
  },
  Inventory: {
    label: "Inventory",
    routerLink: ["/inventory/manage/inventory"],
    icon: "fa fa-warehouse-full",
  },
  Scanner: {
    label: "Scanner",
    routerLink: ["/scanner"],
    icon: "fa-duotone fa-regular fa-scanner-gun",
  },
  // JobOrders: {
  //   label: "Job Orders",
  //   routerLink: ["/job-orders"],
  //   icon: "fa fa-truck-fast",
  // },
  Purchasing: {
    label: "Purchases",
    routerLink: ["/purchasing/manage/purchase-orders"],
    icon: "fa fa-cart-shopping",
  },
  InProcess: {
    label: "In-Process",
    routerLink: ["/in-process/manage/job-orders"],
    icon: "fa fa-arrow-progress",
  },
  EndOfDay: {
    label: "End of Day",
    routerLink: ["/end-of-day/manage/invoices"],
    icon: "fa fa-calendar-check",
  },
  Returns: {
    label: "Returns",
    routerLink: ["/returns-inbox"],
    icon: "fa-regular fa-inbox",
  },
  OrderFacilitation: {
    label: "Facilitation",
    routerLink: ["/order-facilitation/manage"],
    icon: "fa fa-arrows-turn-to-dots",
  },
  Accounts: {
    label: "Accounts",
    routerLink: ["/accounts"],
    icon: "fa fa-buildings",
  },
  Issues: {
    label: "Issues",
    routerLink: ["/entity-issue"],
    icon: "fa fa-square-exclamation",
  },
  Administration: {
    label: "Administration",
    routerLink: ["/administration/manage"],
    icon: "fa fa-screwdriver-wrench",
  },
  GLSubmissions: {
    label: "G/L Submissions",
    routerLink: ["/gl-submission"],
    icon: "fa fa-file-invoice-dollar",
  },
  Templates: {
    label: "Templates",
    routerLink: ["/notifications"],
    icon: "fa fa-envelope",
  },
  User: { id: "user", label: "User", icon: "fa  fa-user" },
  Users: {
    id: "users",
    label: "Access",
    routerLink: ["/user-accounts"],
    icon: "fa fa-users",
  },
  ProximityApi: {
    label: "Proximity API",
    routerLink: ["/proximity-api"],
    icon: "fa fa-server",
  },
  ApiDocs: {
    label: "API Documentation",
    url: environment.apiRoot + "swagger/index.html",
    icon: "fa fa-book",
  },
  QboTest: {
    label: "QBO Test",
    routerLink: ["/qbo-test"],
    icon: "fa fa-cloud",
  },
  Charts: {
    label: "Charts",
    routerLink: ["/charts-container"],
    icon: "fa fa-chart",
  },
  // In Process links
  // JobOrdersInProcess: {
  //   label: "Job Orders In Process",
  //   routerLink: ["/jo-in-process"],
  //   icon: "fa  fa-truck-fast",
  // },
  // AddonBins: {
  //   label: "Addon Bins",
  //   routerLink: ["/addon-bins"],
  //   icon: "fa  fa-truck-fast",
  // },
  // ShipPrep: {
  //   label: "Ship Prep",
  //   routerLink: ["/jo-ship-prep"],
  //   icon: "fa  fa-truck-fast",
  // },
  // Vouchers: {
  //   label: "Vouchers",
  //   routerLink: ["/invoices"],
  //   icon: "fa  fa-conveyor-belt-arm",
  // },
  // ShippingInvoicing: {
  //   label: "Shipping and Invoicing",
  //   routerLink: ["/jo-ship"],
  //   icon: "fa  fa-conveyor-belt-arm",
  // },

  GoBack: { label: "Go Back", icon: "fa  fa-conveyor-belt-arm" },
});

@Injectable({
  providedIn: "root",
})
export class AppNavigationService {
  navigationItems$: Observable<MenuItem[]>;
  showNavigation: boolean = false;

  private navigationItemsSubject = new BehaviorSubject<MenuItem[]>([]);

  constructor() {
    this.navigationItems$ = this.navigationItemsSubject.asObservable();

    this.navigationItemsSubject.next([
      {
        items: [
          NavigationItems.Home,
        ],
      },
      { separator: true },
      {
        items: [
          NavigationItems.Accounts,
          NavigationItems.References,
          NavigationItems.Inventory,
        ],
      },
      { separator: true },
      {
        items: [
          NavigationItems.Purchasing,
          // NavigationItems.Scanner,
          // NavigationItems.JobOrders,
          NavigationItems.InProcess,
          NavigationItems.EndOfDay,
          NavigationItems.Returns,
          NavigationItems.OrderFacilitation,
          NavigationItems.Issues,
        ],
      },
      { separator: true },
      {
        items: [
          NavigationItems.User,
          NavigationItems.Administration,
        ],
      },
    ]);
  }

  show(): void {
    this.showNavigation = true;
  }

  hide(): void {
    this.showNavigation = false;
  }

  getNavigationItem(id: string, navigation = null): any | boolean {
    if (!navigation) {
      navigation = this.navigationItemsSubject.value;
    }

    for (const item of navigation) {
      if (item.id === id) {
        return item;
      }

      if (item.items) {
        const childItem = this.getNavigationItem(id, item.items);

        if (childItem) {
          return childItem;
        }
      }
    }

    return false;
  }

  getNavigationItemParent(id: string, navigation = null, parent = null): any {
    if (!navigation) {
      navigation = this.navigationItemsSubject.value;
      parent = navigation;
    }

    for (const item of navigation) {
      if (item.id === id) {
        return parent;
      }

      if (item.items) {
        const childItem = this.getNavigationItemParent(id, item.items, item);

        if (childItem) {
          return childItem;
        }
      }
    }

    return false;
  }

  removeNavigationItem(id: string): void {
    const item = this.getNavigationItem(id);

    // Return, if there is not such an item
    if (!item) {
      return;
    }

    // Get the parent of the item
    let parent = this.getNavigationItemParent(id);

    // This check is required because of the first level
    // of the navigation, since the first level is not
    // inside the 'children' array
    parent = parent.items || parent;

    // Remove the item
    parent.splice(parent.indexOf(item), 1);

    this.navigationItemsSubject.next(this.navigationItemsSubject.value);
  }

  addNavigationItem(item: MenuItem, id: string): void {
    // Get the current navigation
    const navigation: any[] = this.navigationItemsSubject.value;

    // Add to the end of the navigation
    if (id === "end") {
      navigation.push(item);

      // Trigger the observable
      this.navigationItemsSubject.next(navigation);

      return;
    }

    // Add to the start of the navigation
    if (id === "start") {
      navigation.unshift(item);

      // Trigger the observable
      this.navigationItemsSubject.next(navigation);

      return;
    }

    // Add it to a specific location
    const parent: any = this.getNavigationItem(id);

    if (parent) {
      // Check if parent has a children entry,
      // and add it if it doesn't
      if (!parent.items) {
        parent.items = [];
      }

      // Add the item
      parent.items.push(item);
    }

    // Trigger the observable
    this.navigationItemsSubject.next(navigation);
  }

  setVisible(id: string, visible: boolean): void {
    const item = this.getNavigationItem(id);
    if (!item) {
      return;
    }

    item.visible = visible;
    this.navigationItemsSubject.next(this.navigationItemsSubject.value);
  }
}
